import { BookingFlowLayout } from '@/components/BookingFlowLayout';
import {
  AdminDashboardPage,
  AdminLoginPage,
  BookingDetailsPage,
  HomePage,
  NotFoundPage,
  PaymentPage,
  SkiPassOffersPage,
  TermsAndConditionsPage,
  TripDetailsPage,
} from '@/pages';
import { a2aService } from '@/services/a2a.service';
import { rememberAlps2AlpsUserId } from '@/services/gtm-ecommerce.service';
import { ROUTE_RESTORATION_FLAG } from '@/shared/constants/storage-keys';
import { useBookSkiPassStore } from '@/store/book-ski-pass.store';
import { useEffect, useRef } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

function A2aTokenResolver() {
  const location = useLocation();
  const navigate = useNavigate();
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (resolvedRef.current) return;

    resolvedRef.current = true;

    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (!token) {
      void a2aService.getSession().then((session) => {
        if (session.userId == null) {
          useBookSkiPassStore.getState().setAlps2AlpsUserId(null);
          return;
        }
        rememberAlps2AlpsUserId(session.userId);
      }).catch(() => {
        // no loyalty session; events omit userId
      });
      return;
    }

    void a2aService.resolveToken(token).then((result) => {
      rememberAlps2AlpsUserId(result.userId);
      params.delete('token');
      const remaining = params.toString();
      const newPath = remaining ? `${location.pathname}?${remaining}` : location.pathname;
      void navigate(newPath, { replace: true });
    }).catch(() => {
      // token resolution failed silently; user can log in manually
    });
  }, [location.search, location.pathname, navigate]);

  return null;
}

function RouteRestorer() {
  const location = useLocation();
  const resetStepData = useBookSkiPassStore((state) => state.resetStepData);

  useEffect(() => {
    const hasRestored = sessionStorage.getItem(ROUTE_RESTORATION_FLAG);

    if (hasRestored) {
      return;
    }

    // Keep persisted checkout state when users reopen a deep-link step URL.
    // Only reset funnel state when landing on the homepage explicitly.
    if (location.pathname === '/') {
      resetStepData(1);
    }

    sessionStorage.setItem(ROUTE_RESTORATION_FLAG, 'true');
  }, [location.pathname, resetStepData]);

  return null;
}

export function App() {
  return (
    <BrowserRouter>
      <A2aTokenResolver />
      <RouteRestorer />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
        <Route element={<BookingFlowLayout />}>
          <Route path="/trip-details" element={<TripDetailsPage />} />
          <Route path="/ski-pass-offers" element={<SkiPassOffersPage />} />
          <Route path="/booking-details" element={<BookingDetailsPage />} />
          <Route path="/payment" element={<PaymentPage />} />
        </Route>
        <Route path="/auth/login" element={<Navigate to="/admin/login" replace />} />
        <Route path="/auth/register" element={<Navigate to="/admin/login" replace />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<AdminDashboardPage />} />
        <Route path="/admin/pricing" element={<AdminDashboardPage />} />
        <Route path="/admin/pricing/markup" element={<AdminDashboardPage />} />
        <Route path="/admin/promocodes" element={<AdminDashboardPage />} />
        <Route path="/admin/promocodes/:promoCodeId" element={<AdminDashboardPage />} />
        <Route path="/admin/orders/:orderId" element={<AdminDashboardPage />} />
        <Route path="/admin/content" element={<AdminDashboardPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}
